/**
 * <sample-request> - PDP widget adding the store's hidden sample product to
 * the cart with line item properties recording the sampled product/variant.
 *
 * Tracks the PDP's variant picker via the variantChange pubsub event so the
 * description, availability gating and duplicate state follow the selection.
 * Limits enforced against the live cart (max samples per order, one sample
 * per product+variant). Renders the cart drawer sections on success, same as
 * product-form.js.
 */
class SampleRequest extends HTMLElement {
	constructor() {
		super();
		this.button = this.querySelector('[data-sample-add]');
		this.buttonText = this.querySelector('[data-sample-button-text]');
		this.spinner = this.querySelector('.loading__spinner');
		this.message = this.querySelector('[data-sample-message]');
		this.target = this.querySelector('[data-sample-target]');
		this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

		this.button.addEventListener('click', this.addSample.bind(this));
	}

	connectedCallback() {
		this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
			const variant = event.data.variant;
			if (!variant) return;
			this.dataset.variantId = variant.id;
			this.dataset.variantTitle = variant.title || '';
			this.dataset.variantAvailable = variant.available;

			if (this.target) {
				let label = this.dataset.productTitle;
				if (variant.title && variant.title !== this.dataset.productTitle && variant.title !== 'Default Title') {
					label += ` - ${variant.title}`;
				}
				this.target.textContent = label;
			}

			if (this.dataset.stockOnly === 'true') {
				this.classList.toggle('hidden', !variant.available);
			}

			this.hideMessage();
			this.setAdded(false);
		});
	}

	disconnectedCallback() {
		if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
	}

	async addSample() {
		this.setLoading(true);
		this.hideMessage();

		try {
			const check = await this.checkCart();
			if (!check.canAdd) {
				if (check.reason === 'duplicate') {
					this.showMessage(window.sampleRequestStrings.duplicate, true);
					this.setAdded(true);
				} else {
					this.showMessage(window.sampleRequestStrings.limit.replace('[max]', this.dataset.maxSamples), true);
				}
				return;
			}

			let variantTitle = this.dataset.variantTitle;
			if (!variantTitle || variantTitle === this.dataset.productTitle || variantTitle === 'Default Title') {
				variantTitle = '';
			}

			const body = {
				items: [
					{
						id: Number(this.dataset.sampleVariantId),
						quantity: 1,
						properties: {
							_sample_of_product_id: this.dataset.productId,
							_sample_of_variant_id: this.dataset.variantId,
							_sample_product_title: this.dataset.productTitle,
							_sample_variant_title: variantTitle,
							_sample_product_handle: this.dataset.productHandle,
						},
					},
				],
			};

			if (this.cart) {
				body.sections = this.cart.getSectionsToRender().map((section) => section.id);
				body.sections_url = window.location.pathname;
				this.cart.setActiveElement(document.activeElement);
			}

			const response = await fetch(`${routes.cart_add_url}.js`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			});
			const data = await response.json();

			if (data.status) {
				// Cart API error (e.g. sample product out of stock)
				this.showMessage(data.description || window.sampleRequestStrings.error, true);
				return;
			}

			// [data-added] locks the button (pointer-events: none); with
			// duplicates allowed it must stay clickable to add another
			if (this.dataset.allowDuplicates !== 'true') this.setAdded(true);
			this.showMessage(window.sampleRequestStrings.success, false);

			if (this.cart) {
				// Upsell recommendations key off the sampled product
				localStorage.setItem('lastProductAdded', this.dataset.productId);
				this.cart.renderContents(data);
			}

			if (window.dataLayer) {
				window.dataLayer.push({
					event: 'add_sample_to_cart',
					product_name: this.dataset.productTitle,
					variant_name: variantTitle,
				});
			}
		} catch (error) {
			console.error('[sample-request]', error);
			this.showMessage(window.sampleRequestStrings.error, true);
		} finally {
			this.setLoading(false);
		}
	}

	async checkCart() {
		const response = await fetch(`${routes.cart_url}.js`);
		const cart = await response.json();
		const samples = cart.items.filter((item) => item.properties && item.properties._sample_of_product_id);
		// The max counts units, not lines: a sample line at quantity 2 uses 2
		const totalQuantity = samples.reduce((sum, item) => sum + item.quantity, 0);

		if (this.dataset.allowDuplicates !== 'true') {
			const duplicate = samples.find(
				(item) =>
					item.properties._sample_of_product_id == this.dataset.productId &&
					item.properties._sample_of_variant_id == this.dataset.variantId
			);
			if (duplicate) return { canAdd: false, reason: 'duplicate' };
		}
		if (totalQuantity >= Number(this.dataset.maxSamples)) return { canAdd: false, reason: 'limit' };
		return { canAdd: true };
	}

	setLoading(loading) {
		this.button.toggleAttribute('disabled', loading);
		this.button.classList.toggle('loading', loading);
		this.spinner.classList.toggle('hidden', !loading);
	}

	setAdded(added) {
		this.button.toggleAttribute('data-added', added);
		this.buttonText.textContent = added ? window.sampleRequestStrings.added : window.sampleRequestStrings.add;
	}

	showMessage(text, isError) {
		this.message.textContent = text;
		this.message.classList.remove('hidden');
		this.message.classList.toggle('sample-request__message--error', Boolean(isError));
	}

	hideMessage() {
		this.message.classList.add('hidden');
	}
}

customElements.define('sample-request', SampleRequest);
